// src/pages/DashboardPage.jsx
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getDashboardData } from '../api/apiClient';
import PnlChart from '../components/shared/PnlChart';
import ActivePositionsTable from '../components/shared/ActivePositionsTable';
import RecentTradesTable from '../components/shared/RecentTradesTable';
import {
  HiExclamationCircle,
  HiOutlineRefresh,
  HiOutlineTrendingUp,
  HiOutlineCurrencyDollar,
  HiOutlineStatusOnline,
  HiOutlineClock
} from 'react-icons/hi';

const accentStyles = {
  blue: 'border-blue-500/30 bg-blue-500/10 text-blue-400',
  emerald: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
  amber: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  rose: 'border-rose-500/30 bg-rose-500/10 text-rose-300'
};

const StatCard = ({ title, value, valueColor = 'text-white', meta, loading, icon: Icon, accent = 'blue' }) => (
  <div className="bg-slate-800/80 p-5 rounded-2xl shadow-lg border border-slate-700/60">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <h3 className="text-gray-400 text-[11px] font-semibold uppercase tracking-[0.18em]">
          {title}
        </h3>
        {loading ? (
          <div className="h-8 w-28 bg-slate-700 animate-pulse rounded mt-3"></div>
        ) : (
          <p className={`text-2xl font-bold mt-3 ${valueColor}`}>{value}</p>
        )}
        {meta && <p className="text-xs text-slate-500 mt-2">{meta}</p>}
      </div>
      {Icon && (
        <div className={`h-11 w-11 rounded-2xl border flex items-center justify-center ${accentStyles[accent] || accentStyles.blue}`}>
          <Icon className="h-5 w-5" />
        </div>
      )}
    </div>
  </div>
);

const TimeRangeSwitcher = ({ selectedRange, onSelectRange }) => {
  const { t } = useTranslation();
  const ranges = [
    { key: 'd1', label: t('dashboard.ranges.d1') },
    { key: 'd7', label: t('dashboard.ranges.d7') },
    { key: 'd30', label: t('dashboard.ranges.d30') },
    { key: 'all', label: t('dashboard.ranges.all') },
  ];

  return (
    <div className="flex bg-slate-900/60 border border-slate-700/70 rounded-xl p-1.5 shadow-inner">
      {ranges.map((r) => (
        <button
          key={r.key}
          onClick={() => onSelectRange(r.key)}
          className={`px-4 py-2 text-[11px] font-bold rounded-lg transition-all duration-200 tracking-widest ${
            selectedRange === r.key ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
};

const DashboardPage = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('d1'); 

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getDashboardData();
      setData(res);
      setError(null);
    } catch (err) {
      console.error("Dashboard error:", err);
      setError(t('dashboard.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Вычисляемые данные для отображения
  const stats = useMemo(() => {
    if (!data || !data.pnl || !data.pnl.dynamics) {
        return { pnl: 0, trades: 0, chartData: [] };
    }
    
    const rawDynamics = data.pnl.dynamics[timeRange] || [];
    let runningTotal = 0;

    // Преобразуем данные:
    // 1. Считаем кумулятивный (накопительный) PnL
    // 2. Преобразуем время в Timestamp (ms)
    const chartData = rawDynamics.map(item => {
      runningTotal += (item.v || 0); // Складываем текущее значение с предыдущим
      
      // Обработка разных форматов времени (заменяем пробел на T для Safari/Firefox)
      const timeStr = item.t.includes(' ') ? item.t.replace(' ', 'T') : item.t;
      
      return {
        time: new Date(timeStr).getTime(),
        value: Number(runningTotal.toFixed(4)),
        pnl: Number(runningTotal.toFixed(4)) // Дублируем ключ, если PnlChart ищет 'pnl'
      };
    });

    return {
      pnl: data.pnl[timeRange] || 0,
      trades: data.tradesCount[timeRange] || 0,
      chartData
    };
  }, [data, timeRange]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-white space-y-4">
        <HiExclamationCircle className="w-12 h-12 text-rose-500" />
        <p className="text-xl font-medium">{error}</p>
        <button onClick={fetchData} className="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-500 transition-colors">Retry</button>
      </div>
    );
  }

  const pnlValue = stats.pnl;
  const pnlColor = pnlValue >= 0 ? 'text-emerald-400' : 'text-rose-400';
  const openPositions = data?.openPositions || [];
  const recentTrades = data?.lastTrades || [];
  const openPositionsCount = openPositions.length;
  const recentTradesCount = recentTrades.length;
  const timeRangeLabel = t(`dashboard.ranges.${timeRange}`);

  return (
    <div className="space-y-8 pb-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">{t('dashboard.title')}</h1>
          <p className="text-sm text-slate-400 mt-1">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <TimeRangeSwitcher selectedRange={timeRange} onSelectRange={setTimeRange} />
          <button
            type="button"
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700/70 bg-slate-900/70 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-200 shadow-lg shadow-slate-950/40 transition-colors hover:bg-slate-800 disabled:opacity-60"
          >
            <HiOutlineRefresh className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? t('dashboard.refreshing') : t('dashboard.refresh')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.7fr_1fr] gap-6">
        <div className="relative overflow-hidden rounded-3xl border border-slate-700/60 bg-slate-900/70 p-6 shadow-2xl">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.18),_transparent_55%)]"></div>
          <div className="absolute -bottom-24 right-0 h-60 w-60 rounded-full bg-emerald-500/10 blur-3xl"></div>
          <div className="relative z-10">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                  {t('dashboard.total_pnl')}
                </div>
                {loading ? (
                  <div className="mt-4 h-10 w-40 rounded-xl bg-slate-800 animate-pulse"></div>
                ) : (
                  <div className={`mt-4 text-4xl font-black tracking-tight ${pnlColor}`}>
                    ${pnlValue.toLocaleString('en-US', { minimumFractionDigits: 3 })}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                  <span className="rounded-full border border-slate-700/70 bg-slate-900/60 px-3 py-1 uppercase tracking-widest">
                    {timeRangeLabel}
                  </span>
                  <span className="rounded-full border border-slate-700/70 bg-slate-900/60 px-3 py-1">
                    {t('dashboard.trades_count')}: {stats.trades}
                  </span>
                </div>
              </div>
              <div className={`h-14 w-14 rounded-2xl border flex items-center justify-center ${
                pnlValue >= 0 ? accentStyles.emerald : accentStyles.rose
              }`}>
                <HiOutlineTrendingUp className="h-6 w-6" />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between text-xs">
              <span className="uppercase tracking-[0.22em] text-slate-400">
                {t('dashboard.pnl_dynamics')}
              </span>
              <span className="text-slate-500">{timeRangeLabel}</span>
            </div>
            <div className="mt-3 h-[300px] w-full">
              <PnlChart 
                data={stats.chartData} 
                loading={loading} 
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
          <StatCard
            title={t('dashboard.trades_count')}
            value={Number(stats.trades || 0).toLocaleString('en-US')}
            meta={timeRangeLabel}
            loading={loading}
            icon={HiOutlineTrendingUp}
            accent="blue"
          />
          <StatCard
            title={`${t('dashboard.total_pnl')} (${t('dashboard.ranges.all')})`}
            value={`$${Number(data?.pnl?.all || 0).toLocaleString('en-US', { minimumFractionDigits: 3 })}`}
            valueColor={(data?.pnl?.all || 0) >= 0 ? 'text-blue-400' : 'text-rose-400'}
            loading={loading}
            icon={HiOutlineCurrencyDollar}
            accent={(data?.pnl?.all || 0) >= 0 ? 'blue' : 'rose'}
          />
          <StatCard
            title={t('dashboard.open_positions')}
            value={openPositionsCount.toLocaleString('en-US')}
            loading={loading}
            icon={HiOutlineStatusOnline}
            accent="emerald"
          />
          <StatCard
            title={t('dashboard.recent_trades')}
            value={recentTradesCount.toLocaleString('en-US')}
            loading={loading}
            icon={HiOutlineClock}
            accent="amber"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8">
        <ActivePositionsTable positions={openPositions} loading={loading} />
        <RecentTradesTable trades={recentTrades} loading={loading} />
      </div>
    </div>
  );
};

export default DashboardPage;
